import React, { useState, useRef } from "react";
import axiosInstance from "../api/axiosInstance";
import clientAxiosInstance from "../api/clientAxiosInstance";
import { formatDateForDisplay, formatDateForInput } from "../utils/formatters";
import {
  formatPhoneInput,
  formatZipInput,
  formatSSNInput,
  validateEmail,
  validateSSNDigits,
} from "../utils/validationHelpers";
import StateCitySelect from "./StateCitySelect";
import { COUNTRY_OPTIONS } from "../constants/projectConstants";
import ExistingAddressPicker from "./ExistingAddressPicker";
import DateInput from "./DateInput";
import {
  getDatePickerBoundsForConstraint,
  normalizeToYmd,
  validateDobYmd,
} from "../utils/dateFieldConstraints";
import { GENDER_OPTIONS, RELATIONSHIP_OPTIONS, SUFFIX_OPTIONS, TITLE_OPTIONS } from "../constants/membersConstants";

function AddMemberModal({ isOpen, onClose, household, onSuccess, onHouseholdUpdate, clientToken = null }) {
  const [formData, setFormData] = useState({
    title: "",
    gender: "",
    first_name: "",
    last_name: "",
    suffix: "",
    relationship: "",
    ssn_or_tin: "",
    dob: "",
    email: "",
    country_of_citizenship: "United States",
    address_line_1: "",
    address_line_2: "",
    city: "",
    province: "",
    postal_code: "",
    country: "USA",
    phone: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [addressPickerKey, setAddressPickerKey] = useState(0);
  const dateInputRef = useRef(null);

  const titleOptions = TITLE_OPTIONS;

  const handleChange = (field, value) => {
    let sanitizedValue = value;
    if (field === "phone") {
      sanitizedValue = formatPhoneInput(value);
    } else if (field === "postal_code") {
      sanitizedValue = formatZipInput(value);
    } else if (field === "ssn_or_tin") {
      sanitizedValue = formatSSNInput(value);
    }
    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    setError("");
  };


  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError("First name is required");
      return false;
    }
    if (!formData.last_name.trim()) {
      setError("Last name is required");
      return false;
    }
    if (!formData.relationship) {
      setError("Relationship to primary member is required");
      return false;
    }
    if (formData.email && !validateEmail(formData.email.trim())) {
      setError("Please enter a valid email address");
      return false;
    }
    if (
      formData.ssn_or_tin &&
      formData.ssn_or_tin.trim() &&
      !validateSSNDigits(formData.ssn_or_tin.trim())
    ) {
      setError("Please enter a valid 9-digit SSN/TIN");
      return false;
    }
    if (formData.dob?.trim()) {
      const dobMsg = validateDobYmd(normalizeToYmd(formData.dob), false);
      if (dobMsg) {
        setError(dobMsg);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError("");

    try {
      const personal_info = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        relationship: formData.relationship
      };

      if (formData.title) {
        personal_info.title = formData.title;
      }

      if (formData.gender) {
        personal_info.gender = formData.gender;
      }

      if (formData.suffix) {
        personal_info.suffix = formData.suffix;
      }

      if (formData.ssn_or_tin && formData.ssn_or_tin.trim()) {
        personal_info.ssn_or_tin = formData.ssn_or_tin.trim();
      }

      if (formData.dob) {
        personal_info.dob = formatDateForInput(formData.dob);
      }

      if (formData.email && formData.email.trim()) {
        personal_info.email = formData.email.trim();
      }

      if (formData.country_of_citizenship && formData.country_of_citizenship.trim()) {
        personal_info.country_of_citizenship = formData.country_of_citizenship.trim();
      }

      // Add address if at least one address field is filled
      const hasAddress = formData.address_line_1 || formData.address_line_2 || 
                        formData.city || formData.province || formData.postal_code || 
                        formData.country || formData.phone;

      if (hasAddress) {
        personal_info.address = [{
          type: "home",
          ...(formData.address_line_1 && { address_line_1: formData.address_line_1 }),
          ...(formData.address_line_2 && { address_line_2: formData.address_line_2 }),
          ...(formData.city && { city: formData.city }),
          ...(formData.province && { province: formData.province }),
          ...(formData.postal_code && { postal_code: formData.postal_code }),
          ...(formData.country && { country: formData.country }),
          ...(formData.phone && { phone: formData.phone })
        }];
      }

      const response = await (clientToken ? clientAxiosInstance : axiosInstance).post(
        clientToken 
          ? `/client/household/${household.id}/members`
          : `/households/${household.id}/members`,
        { personal_info },
        clientToken ? {
          headers: {
            Authorization: `Bearer ${clientToken}`
          }
        } : undefined
      );

      // Reset form
      setFormData({
        title: "",
        gender: "",
        first_name: "",
        last_name: "",
        suffix: "",
        relationship: "",
        ssn_or_tin: "",
        dob: "",
        email: "",
        country_of_citizenship: "United States",
        address_line_1: "",
        address_line_2: "",
        city: "",
        province: "",
        postal_code: "",
        country: "USA",
        phone: ""
      });

      // Trigger silent refresh to get updated household data
      if (onHouseholdUpdate) {
        onHouseholdUpdate(null);
      }

      // Call success callback with new member id
      onSuccess(response.data.id);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(", "));
      } else {
        setError("Failed to create household member. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: "",
      first_name: "",
      last_name: "",
      suffix: "",
      relationship: "",
      ssn_or_tin: "",
      dob: "",
      email: "",
      country_of_citizenship: "United States",
      address_line_1: "",
      address_line_2: "",
      city: "",
      province: "",
      postal_code: "",
      country: "USA",
      phone: ""
    });
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  const dobPickerBounds = getDatePickerBoundsForConstraint("dob_non_primary");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Add Household Member</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={saving}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <select
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                >
                  <option value="">Select title</option>
                  {titleOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  value={formData.gender || ""}
                  onChange={(e) => handleChange("gender", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                >
                  <option value="">Select gender</option>
                  {GENDER_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter first name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter last name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Suffix
                </label>
                <select
                  value={formData.suffix || ""}
                  onChange={(e) => handleChange("suffix", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                >
                  <option value="">None</option>
                  {SUFFIX_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship to Primary Member <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.relationship}
                  onChange={(e) => handleChange("relationship", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                >
                  <option value="">Select relationship</option>
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SSN/TIN
                </label>
                <input
                  type="text"
                  value={formData.ssn_or_tin}
                  onChange={(e) => handleChange("ssn_or_tin", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter SSN or TIN"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <DateInput
                  value={formData.dob ? formatDateForInput(formData.dob) : ""}
                  onChange={(val) => handleChange("dob", val)}
                  placeholder="mm-dd-yyyy"
                  disabled={saving}
                  minDate={dobPickerBounds.minDate}
                  maxDate={dobPickerBounds.maxDate}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country of Citizenship
                </label>
                <select
                  value={formData.country_of_citizenship}
                  onChange={(e) => handleChange("country_of_citizenship", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                >
                  <option value="">Select country</option>
                  {COUNTRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Address</h3>
            <ExistingAddressPicker
              household={household}
              getField={(k) => formData[k]}
              setField={(k, v) => handleChange(k, v)}
              onAfterSelect={() => setAddressPickerKey((k) => k + 1)}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={formData.address_line_1}
                  onChange={(e) => handleChange("address_line_1", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter address line 1"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.address_line_2}
                  onChange={(e) => handleChange("address_line_2", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter address line 2"
                />
              </div>

              <div className="md:col-span-2">
                <StateCitySelect
                  key={addressPickerKey}
                  countryValue={formData.country}
                  stateValue={formData.province}
                  cityValue={formData.city}
                  onCountryChange={(val) => handleChange("country", val)}
                  onStateChange={(val) => handleChange("province", val)}
                  onCityChange={(val) => handleChange("city", val)}
                  isEditing={true}
                  disabled={saving}
                  stateLabel="State"
                  cityLabel="City"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => handleChange("postal_code", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter postal code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  disabled={saving}
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding...
              </>
            ) : (
              "Add Member"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddMemberModal;
